/**
 * The computer admiral. Ported from legacy/admirals-return.html
 * (aiBuyFleet / aiMoveAll / aiBuild), but rebuilt to be **fog-honest**: it only
 * reasons about enemy ships through fog.ts's `perceivedEnemies`, and never moves
 * with the `seeAll` cheat flag. eslint enforces both (see eslint.config.mjs).
 * Combat target selection is left to the engine's default during resolveCombat,
 * which acts on the real board for both sides equally — not an AI planning read.
 *
 * Four jobs, in the legacy order: BUY a fleet, EXPAND/ESCORT with transports,
 * HUNT with warships, BUILD at shipyards. Difficulty is parameterized now
 * (budget, fog honesty, targeting noise); the 11-level table lands in WP-11.
 */
import { hexDist } from './board';
import { separateShips } from './deploy';
import { perceivedEnemies, visibleOnBoard } from './fog';
import { executeMove, pathTo, reachable } from './movement';
import { addShip, atkKey, defKey, DMG, isTransportClass, UNITS } from './ships';
import { cityAt, stateRng, type City, type GameState, type Ship, type ShipType, type Side } from './state';
import { purchaseShip } from './turn';

export interface AIProfile {
  /** scales the starting points endowment. NB: a bigger fleet self-congests
   *  under one-per-tile, so this is NOT a strength axis — difficulty keeps it
   *  flat and scales `perception` instead. */
  budgetMult: number;
  /** 0..1 map awareness: 0 = fully fog-honest (sees only what fog reveals),
   *  1 = omniscient. The difficulty axis that reliably converts to wins. */
  perception: number;
  /** 0..1 chance to take a random rather than best target/destination */
  targetingNoise: number;
}

export const DEFAULT_AI: AIProfile = { budgetMult: 1, perception: 0, targetingNoise: 0 };

/** The 11 difficulty levels the game exposes (manual: levels 1–11; "Gold patch
 *  requires level 8+"). Under the one-per-tile / single-currency rework the only
 *  axis that reliably converts to wins is MAP AWARENESS — a bigger fleet just
 *  self-congests, and fog-honest tactical play tops out near 50% by symmetry.
 *  So strength scales with `perception`:
 *   - levels 1–4 are fully fog-honest (perception 0); from 5 up it ramps to a
 *     fully omniscient Grand Admiral (perception 1 at level 11);
 *   - budgetMult stays flat (1.0) — scaling it up would HURT (congestion);
 *   - targetingNoise falls 0.60 → 0 as light flavour (crisper picks).
 *  Verified by the difficulty sim matrix (win rate vs a fixed opponent rises
 *  with level) — tests/ai.test.ts. */
export const MAX_DIFFICULTY = 11;
export const DEFAULT_DIFFICULTY = 6;

/** Short rank name shown in the difficulty picker (1 = easiest). */
export const DIFFICULTY_NAMES: readonly string[] = [
  'Cadet', 'Ensign', 'Lieutenant', 'Lt. Commander', 'Commander', 'Captain',
  'Commodore', 'Rear Admiral', 'Vice Admiral', 'Admiral', 'Grand Admiral',
];

export function clampDifficulty(level: number): number {
  return Math.max(1, Math.min(MAX_DIFFICULTY, Math.round(level)));
}

/** Map a 1–11 difficulty level to an AIProfile (see DIFFICULTY_NAMES). */
export function difficultyProfile(level: number): AIProfile {
  const L = clampDifficulty(level);
  const t = (L - 1) / (MAX_DIFFICULTY - 1); // 0..1
  return {
    budgetMult: 1, // flat — a bigger fleet self-congests, so budget isn't a strength axis
    targetingNoise: Math.round(Math.max(0, 0.6 - 0.6 * t) * 1000) / 1000, // 0.60 → 0
    // levels 1–4 fully fog-honest; ramps to omniscient (1.0) by level 11
    perception: Math.round(Math.max(0, (L - 4) / 7) * 1000) / 1000,
  };
}

/** Opening shopping list, then top up with cheap hulls (legacy order). */
const WANT: ShipType[] = ['TR', 'TR', 'TR', 'DD', 'SS', 'BB', 'DD', 'PT', 'TR', 'CV', 'SS', 'AT', 'PT', 'CR', 'TR', 'DD'];

/**
 * A balanced opening fleet affordable on `money`: the legacy want-list, then
 * cheap hulls until broke. Pure (no placement) so the WP-7 buy screen can offer
 * it as a one-click "suggested fleet" too. Returns `{ buys, spent }`.
 */
export function suggestFleet(money: number): { buys: ShipType[]; spent: number } {
  let left = money;
  const buys: ShipType[] = [];
  for (const t of WANT) {
    if (left >= UNITS[t].cost) {
      buys.push(t);
      left -= UNITS[t].cost;
    }
  }
  while (left >= UNITS.PT.cost) {
    const t: ShipType = left >= UNITS.DD.cost ? 'DD' : 'PT';
    buys.push(t);
    left -= UNITS[t].cost;
  }
  return { buys, spent: money - left };
}

export function aiBuyFleet(g: GameState, side: Side, p: AIProfile = DEFAULT_AI): void {
  const homes = g.cities.filter((c) => c.home === side);
  if (!homes.length) return;
  // budgetMult scales the starting points endowment (harder admirals open richer).
  const money = Math.floor(g.vp[side] * p.budgetMult);
  g.vp[side] = money;
  const { buys, spent } = suggestFleet(money);
  buys.forEach((t, i) => {
    const h = homes[i % homes.length]!;
    addShip(g, side, t, h.c, h.r);
  });
  separateShips(g); // one ship per tile — spread the fleet off its home ports
  g.vp[side] -= spent; // whatever's left carries as opening score
}

/** Move `s` toward (tc,tr): pick the reachable, stoppable tile closest to the
 *  target (staying put if nothing is nearer). This lands a hunter ADJACENT to an
 *  occupied enemy tile (which is impassable), and a transport next to a blocked
 *  city, instead of failing when pathTo can't reach the target tile itself.
 *  `avoidCities` keeps warships OFF city tiles — under one-per-tile a warship
 *  parked on a city blocks the transport that must sit there to score, so
 *  warships screen from an adjacent tile instead. */
function advance(g: GameState, s: Ship, tc: number, tr: number, avoidCities = false): void {
  // Route around enemies the AI can actually see (its ships' sight radius), same as
  // the human — otherwise it sails straight through an adjacent enemy it can't
  // "carrier-spot". Wall-avoidance uses the board view; strategic awareness (which
  // enemies to hunt) still flows through perceivedEnemies + the difficulty dial.
  const reach = reachable(g, s, false, visibleOnBoard);
  let bestDest: { c: number; r: number } | null = null;
  let bestD = avoidCities && cityAt(g, s.c, s.r)
    ? Infinity // sitting on a city is never acceptable for a warship — always move off
    : hexDist(s.c, s.r, tc, tr); // distance if we don't move
  for (const o of Object.values(reach)) {
    if (o.stop) continue; // can't end here (friendly ship occupies it)
    if (avoidCities && cityAt(g, o.c, o.r)) continue; // cities are transport real estate
    const d = hexDist(o.c, o.r, tc, tr);
    if (d < bestD) {
      bestD = d;
      bestDest = o;
    }
  }
  if (!bestDest) return;
  const path = pathTo(g, s, bestDest.c, bestDest.r, false, visibleOnBoard);
  if (path && path.length) executeMove(g, s, path);
}

export function aiMoveAll(g: GameState, side: Side, p: AIProfile = DEFAULT_AI): void {
  const rng = stateRng(g);
  const mine = g.ships.filter((s) => s.side === side);
  const foes = perceivedEnemies(g, side, p.perception);

  // --- transports: expand to hold cities from an ADJACENT tile (cities are
  // impassable land — you capture/hold by sitting next to the harbour) ---
  const claimed = new Set<string>();
  const ck = (ct: City): string => `${ct.c},${ct.r}`;
  // fog-honest: only enemies the AI can perceive count as contesting a city
  const enemyAdjCity = (ct: City): boolean => foes.some((e) => hexDist(e.c, e.r, ct.c, ct.r) === 1);
  for (const t of mine.filter(isTransportClass)) {
    // already sitting next to an uncontested city → hold it (no move); a surplus
    // transport (e.g. several near a home port) falls through to expand elsewhere
    const holding = g.cities.find(
      (ct) => hexDist(t.c, t.r, ct.c, ct.r) === 1 && !claimed.has(ck(ct)) && !enemyAdjCity(ct),
    );
    if (holding) {
      claimed.add(ck(holding));
      continue;
    }
    let best: City | null = null;
    let bestScore = -Infinity;
    const cands = g.cities.filter((ct) => !claimed.has(ck(ct)));
    for (const ct of cands) {
      const d = hexDist(t.c, t.r, ct.c, ct.r);
      const v = ct.vp * (ct.home != null && ct.home !== side ? 2 : 1);
      let sc = v / (d + 1);
      if (ct.owner === side) sc -= 60; // already ours — look elsewhere
      else if (ct.owner != null) sc -= 40; // contesting an enemy-held city is costlier
      sc += (rng.next() - 0.5) * p.targetingNoise * 200;
      if (sc > bestScore) {
        bestScore = sc;
        best = ct;
      }
    }
    if (best) {
      claimed.add(ck(best));
      advance(g, t, best.c, best.r); // lands ADJACENT (the city tile is impassable)
    }
  }

  // --- warships: hunt the juiciest reachable enemy, else escort/advance ---
  // (forts are immobile — they defend in place and never feature here)
  const transports = mine.filter(isTransportClass);
  for (const w of mine.filter((s) => !isTransportClass(s) && UNITS[s.type].move > 0)) {
    let best: Ship | null = null;
    let bestScore = 0;
    for (const e of foes) {
      const dmg = DMG[atkKey(w)][defKey(e)] ?? 0;
      if (dmg <= 0) continue;
      const d = hexDist(w.c, w.r, e.c, e.r);
      let sc = dmg * 3 + UNITS[e.type].cost - d * 6;
      if (dmg >= e.hp) sc += 25;
      if (isTransportClass(e)) sc += 20;
      sc += (rng.next() - 0.5) * p.targetingNoise * 60;
      if (sc > bestScore) {
        bestScore = sc;
        best = e;
      }
    }
    if (best) {
      if (hexDist(w.c, w.r, best.c, best.r) === 1) continue; // already adjacent — hold to fight
      advance(g, w, best.c, best.r, true);
      continue;
    }
    // nothing worth chasing: shepherd the nearest friendly transport, else push
    // toward the highest-value city not yet ours (screening it from an adjacent
    // tile so an enemy transport there can be sunk and the city freed to capture)
    const guard = transports
      .filter((x) => g.ships.includes(x))
      .sort((a, b) => hexDist(w.c, w.r, a.c, a.r) - hexDist(w.c, w.r, b.c, b.r))[0];
    if (guard) {
      advance(g, w, guard.c, guard.r, true);
      continue;
    }
    const target = g.cities
      .filter((c) => c.owner !== side)
      .sort((a, b) => b.vp / (hexDist(w.c, w.r, b.c, b.r) + 1) - a.vp / (hexDist(w.c, w.r, a.c, a.r) + 1))[0];
    if (target) advance(g, w, target.c, target.r, true);
  }

  aiBuild(g, side);
}

export function aiBuild(g: GameState, side: Side): void {
  const yards = g.cities.filter((c) => c.owner === side && c.vp === 200);
  if (!yards.length) return;
  // Live counts (recomputed each read) so multiple yards in one turn don't all
  // buy the same type — each purchase shifts the deficits the next yard sees.
  const count = (t: ShipType): number => g.ships.filter((s) => s.side === side && s.type === t).length;
  const transports = (): number => g.ships.filter((s) => s.side === side && isTransportClass(s)).length;
  const warships = (): number => g.ships.filter((s) => s.side === side && !isTransportClass(s) && s.type !== 'FT').length;
  // Enemy composition we react to is fog-honest too (own ships always known).
  const foes = perceivedEnemies(g, side, 0);
  const enemySubs = foes.some((s) => s.type === 'SS');
  const enemyCaps = foes.some((s) => s.type === 'BB' || s.type === 'CV');
  const hasFort = (y: City): boolean =>
    g.ships.some((s) => s.side === side && s.type === 'FT' && hexDist(s.c, s.r, y.c, y.r) <= 1);
  // A 200-point yard with an enemy bearing down (≤3 hexes) and no fort yet gets a
  // Fort — a cheap immobile gun emplacement that guards the harbour approach.
  const threatened = (y: City): boolean => foes.some((e) => hexDist(e.c, e.r, y.c, y.r) <= 3);
  // Transports SCORE (they hold cities), so keep a working corps of them scaled to
  // the map. The killing arm is built to a target MIX — we buy whichever warship
  // class is furthest below its target share, so the fleet stays balanced instead
  // of endlessly stamping out one cheap hull (the old fallback DD/CR-spam).
  const wantTR = Math.min(6, 2 + Math.floor(g.cities.length / 5));
  const share: Partial<Record<ShipType, number>> = { DD: 0.38, CR: 0.30, SS: 0.20, CV: 0.12 };
  if (enemySubs) share.DD! += 0.12; // more screens against a torpedo threat
  if (enemyCaps) share.SS! += 0.12; // more subs to answer capital ships
  const warTypes: ShipType[] = ['DD', 'CR', 'SS', 'CV'];
  for (const y of yards) {
    const wish: ShipType[] = [];
    if (threatened(y) && !hasFort(y)) wish.push('FT');   // defend the yard first
    if (transports() < wantTR) wish.push('TR');          // expand & score
    const w = Math.max(1, warships());
    const deficit = (t: ShipType): number => (share[t] ?? 0) - count(t) / w;
    wish.push(...[...warTypes].sort((a, b) => deficit(b) - deficit(a)), 'DD', 'TR');
    for (const t of wish) {
      if (purchaseShip(g, y, t, side)) break; // one purchase per yard per turn
    }
  }
}

/** Convenience: a full AI move function for a side (buy is separate, at setup). */
export function aiMover(p: AIProfile = DEFAULT_AI): (g: GameState, side: Side) => void {
  return (g, side) => aiMoveAll(g, side, p);
}

/** Set up both fleets and kick the game into the move phase. */
export function aiSetup(g: GameState, pR: AIProfile = DEFAULT_AI, pY: AIProfile = DEFAULT_AI): void {
  aiBuyFleet(g, 'R', pR);
  aiBuyFleet(g, 'Y', pY);
  g.phase = 'move';
}
